/**
 * Campaign Manager - Create and manage advertising/promotional campaigns
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone, Plus, Calendar, Target, Users, Eye, TrendingUp,
  MoreVertical, Edit2, Trash2, X, Check, Play, Pause, ChevronRight,
  BarChart3, Globe, Smartphone, Link2, ExternalLink, Tag
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' },
  active: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  paused: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' }
};

const CampaignCard = ({ campaign, onEdit, onDelete, onViewDetails }) => {
  const [showMenu, setShowMenu] = useState(false);
  const status = STATUS_COLORS[campaign.status] || STATUS_COLORS.draft;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl border border-border/50 p-5 hover:shadow-lg transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: campaign.color + '20', color: campaign.color }}
          >
            <Megaphone className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{campaign.name}</h3>
            {campaign.client_name && (
              <p className="text-sm text-muted-foreground">{campaign.client_name}</p>
            )}
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[140px] z-50">
                <button
                  onClick={() => { setShowMenu(false); onViewDetails(campaign); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                >
                  <BarChart3 className="w-4 h-4" /> View Details
                </button>
                <button
                  onClick={() => { setShowMenu(false); onEdit(campaign); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => { setShowMenu(false); onDelete(campaign); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-secondary text-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {campaign.description && (
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{campaign.description}</p>
      )}

      <div className="flex items-center gap-2 mb-4">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary">
          <Tag className="w-3 h-3" />
          {campaign.tag_count} tags
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border/50">
        <div className="text-center">
          <div className="text-lg font-bold">{campaign.total_scans.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">Scans</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold">{campaign.unique_visitors.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">Unique</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold">
            {campaign.total_scans > 0 
              ? Math.round((campaign.unique_visitors / campaign.total_scans) * 100) 
              : 0}%
          </div>
          <div className="text-xs text-muted-foreground">Engagement</div>
        </div>
      </div>

      <button
        onClick={() => onViewDetails(campaign)}
        className="w-full mt-4 py-2.5 bg-secondary hover:bg-secondary/80 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
      >
        View Analytics <ChevronRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

const CampaignModal = ({ isOpen, onClose, campaign, onSave, availableTags }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clientName, setClientName] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);

  const COLORS = [
    '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
    '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'
  ];

  useEffect(() => {
    if (campaign) {
      setName(campaign.name || '');
      setDescription(campaign.description || '');
      setClientName(campaign.client_name || '');
      setColor(campaign.color || '#6366f1');
      setStartDate(campaign.start_date || '');
      setEndDate(campaign.end_date || '');
      setSelectedTags(campaign.tags || []);
    } else {
      setName('');
      setDescription('');
      setClientName('');
      setColor('#6366f1');
      setStartDate('');
      setEndDate('');
      setSelectedTags([]);
    }
  }, [campaign, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }
    setSaving(true);
    await onSave({
      name: name.trim(),
      description: description.trim(),
      client_name: clientName.trim() || null,
      color,
      start_date: startDate || null,
      end_date: endDate || null,
      tags: selectedTags
    });
    setSaving(false);
  };

  const toggleTag = (tagCode) => {
    setSelectedTags(prev => 
      prev.includes(tagCode) 
        ? prev.filter(t => t !== tagCode)
        : [...prev, tagCode]
    );
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          style={{ zIndex: 10000 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
            <h3 className="font-semibold text-lg">
              {campaign ? 'Edit Campaign' : 'Create Campaign'}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-5">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium mb-2">Campaign Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Summer Sale 2026"
                className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
                data-testid="campaign-name-input"
              />
            </div>

            {/* Client Name (for white-label) */}
            <div>
              <label className="block text-sm font-medium mb-2">Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g., Fashion Retail Co"
                className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">For white-label: your client's company name</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Campaign goals and details..."
                rows={3}
                className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium mb-2">Brand Color</label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-lg transition-all ${
                      color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Tag Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Tags in Campaign</label>
                <button
                  type="button"
                  onClick={() => setShowTagSelector(!showTagSelector)}
                  className="text-xs text-primary hover:underline"
                >
                  {showTagSelector ? 'Hide' : 'Select Tags'}
                </button>
              </div>
              
              {selectedTags.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedTags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-lg text-xs"
                    >
                      {tag}
                      <button type="button" onClick={() => toggleTag(tag)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No tags selected</p>
              )}

              {showTagSelector && availableTags && (
                <div className="mt-2 max-h-40 overflow-y-auto bg-secondary/50 rounded-xl p-3">
                  <div className="grid grid-cols-2 gap-2">
                    {availableTags.map(tag => (
                      <label
                        key={tag.nfc_code}
                        className="flex items-center gap-2 p-2 bg-card rounded-lg cursor-pointer hover:bg-secondary"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTags.includes(tag.nfc_code)}
                          onChange={() => toggleTag(tag.nfc_code)}
                          className="rounded"
                        />
                        <span className="text-xs truncate">{tag.title || tag.nfc_code}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 bg-secondary rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-campaign-btn"
              >
                {saving ? 'Saving...' : <><Check className="w-4 h-4" /> {campaign ? 'Update' : 'Create'}</>}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const CampaignManager = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadCampaigns();
    loadTags();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/tags`);
      setAvailableTags(response.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleCreateCampaign = async (data) => {
    try {
      await axios.post(`${API}/api/admin/campaigns`, data);
      toast.success('Campaign created!');
      setShowModal(false);
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const handleUpdateCampaign = async (data) => {
    try {
      await axios.put(`${API}/api/admin/campaigns/${editingCampaign.campaign_id}`, data);
      // Update tags separately
      await axios.post(`${API}/api/admin/campaigns/${editingCampaign.campaign_id}/tags`, data.tags);
      toast.success('Campaign updated!');
      setEditingCampaign(null);
      setShowModal(false);
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete "${campaign.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/campaigns/${campaign.campaign_id}`);
      toast.success('Campaign deleted');
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleViewDetails = (campaign) => {
    navigate(`/admin/campaigns/${campaign.campaign_id}`);
  };

  const filteredCampaigns = statusFilter === 'all' 
    ? campaigns 
    : campaigns.filter(c => c.status === statusFilter);

  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    totalScans: campaigns.reduce((sum, c) => sum + c.total_scans, 0),
    totalUnique: campaigns.reduce((sum, c) => sum + c.unique_visitors, 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary" />
            Campaigns
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Manage advertising and promotional campaigns
          </p>
        </div>
        <button
          onClick={() => { setEditingCampaign(null); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4" />
          New Campaign
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-xs text-muted-foreground">Total Campaigns</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Play className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.active}</div>
              <div className="text-xs text-muted-foreground">Active</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.totalScans.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Total Scans</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.totalUnique.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Unique Visitors</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {['all', 'active', 'draft', 'paused', 'completed'].map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === status 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && (
              <span className="ml-1.5 opacity-70">
                ({campaigns.filter(c => c.status === status).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Campaign Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-pulse text-muted-foreground">Loading campaigns...</div>
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
          <Megaphone className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg mb-2">No campaigns yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first campaign to start tracking promotional performance
          </p>
          <button
            onClick={() => { setEditingCampaign(null); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map(campaign => (
            <CampaignCard
              key={campaign.campaign_id}
              campaign={campaign}
              onEdit={(c) => { setEditingCampaign(c); setShowModal(true); }}
              onDelete={handleDeleteCampaign}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <CampaignModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingCampaign(null); }}
        campaign={editingCampaign}
        onSave={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
        availableTags={availableTags}
      />
    </div>
  );
};

export default CampaignManager;
